import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatCPF = (value) => {
  if (!value || value === "") return "";
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2");
  }
  return value;
};

export const formatPhoneBRLive = (value) => {
  let digits = (value || "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  // remove prefixo do país +55 se presente
  if (digits.length > 11 && digits.startsWith("55")) {
    digits = digits.slice(2);
  }
  const d2 = digits.slice(0, 2);
  const rest = digits.slice(2, 11);
  if (rest.length <= 4) {
    return `(${d2}) ${rest}`;
  }
  if (rest.length <= 8) {
    return `(${d2}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  return `(${d2}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
};

export const mapDataToForm = (data, cpfDigits) => {
  const processos = data.processos || data.processos_cedente || [];

  // Agrega valores de todos os processos para exibição no ValueDisplay
  const totals = processos.reduce(
    (acc, p) => ({
      valor_proposta_final:
        acc.valor_proposta_final + (Number(p.valor_proposta_final) || 0),
      valor_credito: acc.valor_credito + (Number(p.valor_credito) || 0),
      valor_parcelado: acc.valor_parcelado + (Number(p.valor_parcelado) || 0),
      valor_parcela: acc.valor_parcela + (Number(p.valor_parcela) || 0),
      valor_parcela_primeiro_periodo:
        acc.valor_parcela_primeiro_periodo +
        (Number(p.valor_parcela_primeiro_periodo) || 0),
      valor_parcela_segundo_periodo:
        acc.valor_parcela_segundo_periodo +
        (Number(p.valor_parcela_segundo_periodo) || 0),
      valor_parcela_terceiro_periodo:
        acc.valor_parcela_terceiro_periodo +
        (Number(p.valor_parcela_terceiro_periodo) || 0),
    }),
    {
      valor_proposta_final: 0,
      valor_credito: 0,
      valor_parcelado: 0,
      valor_parcela: 0,
      valor_parcela_primeiro_periodo: 0,
      valor_parcela_segundo_periodo: 0,
      valor_parcela_terceiro_periodo: 0,
    },
  );

  return {
    nomeCompleto: data.nome || "",
    cpf: formatCPF(cpfDigits) || formatCPF(data.cpf),
    falecido: Boolean(data.falecido),
    rg: data.rg || "",
    email: data.email || "",
    telefone: data.telefone || "",
    logradouro: data.endereco_logradouro || "",
    numero: data.endereco_numero || "",
    bairro: data.endereco_bairro || "",
    complemento: data.endereco_complemento || "",
    cep: data.endereco_cep || "",
    cidade: data.endereco_cidade || "",
    estado: data.endereco_estado || "",
    parcelas: data.parcelas || 19,
    valorTotalCredito: totals.valor_credito || 0,
    valorAcordo:
      totals.valor_parcelado > 0
        ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(totals.valor_parcelado)
        : "",
    valorParcela:
      totals.valor_parcela > 0
        ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(totals.valor_parcela)
        : "",
    valorCessao:
      totals.valor_proposta_final > 0
        ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(totals.valor_proposta_final)
        : "",
    dataPrimeiraParcela: data.data_primeira_parcela || "",
    dataUltimaParcela: data.data_ultima_parcela || "",
    valorPrimeiraParcela: totals.valor_parcela_primeiro_periodo || 0,
    valorSegundaParcela: totals.valor_parcela_segundo_periodo || 0,
    valorTerceiraParcela: totals.valor_parcela_terceiro_periodo || 0,
    valorTotalParcelado:
      (
        totals.valor_parcela_primeiro_periodo +
        6 * totals.valor_parcela_segundo_periodo +
        12 * totals.valor_parcela_terceiro_periodo
      ).toFixed(2) || "0.00",
    estadoCivil: data.estado_civil || "",
    nome_conjuge: data.nome_conjuge || "",
    cpf_conjuge: formatCPF(data.cpf_conjuge || ""),
    data_nascimento: data.data_nascimento || "",
    processos: processos,
    pendente_documentacao: Boolean(data.pendente_documentacao),
  };
};

export const isValidEmail = (value) => {
  if (!value) return false;
  return /.+@.+\..+/.test(value);
};

export const isValidPhoneBR = (value) => {
  if (!value) return false;
  const digits = (value || "").replace(/\D/g, "");
  return digits.length === 11;
};

export const isValidCEP = (value) => {
  if (!value) return false;
  return /^\d{5}-?\d{3}$/.test(value);
};

export const isValidUF = (value) => {
  if (!value) return false;
  return /^[A-Z]{2}$/.test(value);
};

export const isValidCPF = (cpf) => {
  if (typeof cpf !== "string") return false;
  cpf = cpf.replace(/[.-]/g, "");
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  let remainder;
  for (let i = 1; i <= 9; i++)
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  return true;
};

export const formatDateBR = (value) => {
  if (!value) {
    return "";
  }

  const dObj = new Date(value);

  if (isNaN(dObj.getTime())) {
    return "";
  }

  return dObj.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

export const formatMonthYear = (dateString) => {
  if (!dateString) return "";
  // Adiciona T00:00:00 para garantir que a data seja interpretada em UTC
  const date = new Date(`${dateString}T00:00:00`);
  if (isNaN(date.getTime())) return "";

  const month = date
    .toLocaleString("pt-BR", { month: "short", timeZone: "UTC" })
    .replace(".", "");
  const year = date.getUTCFullYear().toString().slice(-2);

  return `${month}/${year}`;
};

export const formatCurrencyBRL = (value) => {
  if (value == null || isNaN(value)) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

/**
 * Formata a informação de parcelamento com base nos dados do usuário ou processo.
 * @param {Object} data - Objeto contendo os dados de parcelamento.
 * @returns {string} - Texto formatado da parcela.
 */
export const calculateParcelInfo = (data) => {
  if (!data) return "";

  const {
    dataPrimeiraParcela,
    data_primeira_parcela,
    dataUltimaParcela,
    data_ultima_parcela,
    valorTotalCredito,
    valor_credito,
    valorTotalParcelado,
    valor_parcelado,
    parcelas,
    valorPrimeiraParcela,
    valor_parcela_primeiro_periodo,
    valorSegundaParcela,
    valor_parcela_segundo_periodo,
    valorTerceiraParcela,
    valor_parcela_terceiro_periodo,
  } = data;

  const firstDate = dataPrimeiraParcela || data_primeira_parcela;
  const lastDate = dataUltimaParcela || data_ultima_parcela;

  const creditBase = Number(valorTotalCredito || valor_credito) || 0;
  const parceladoBase = Number(valorTotalParcelado || valor_parcelado) || 0;
  const scale = parceladoBase > 0 && creditBase > 0 ? creditBase / parceladoBase : 1;

  const rawV1 = Number(valorPrimeiraParcela || valor_parcela_primeiro_periodo) || 0;
  const rawV2 = Number(valorSegundaParcela || valor_parcela_segundo_periodo) || 0;
  const rawV3 = Number(valorTerceiraParcela || valor_parcela_terceiro_periodo) || 0;

  const v1 = rawV1 * scale;
  const v2 = rawV2 * scale;
  const v3 = rawV3 * scale;

  const totalParcelas = Number(parcelas) || 0;

  if (totalParcelas === 1 || firstDate === lastDate) {
    const dateText = firstDate ? ` em ${formatDateBR(firstDate)}` : "";
    return `Valor será pago por meio de parcela única${dateText}`;
  }

  const addMonths = (dateStr, months) => {
    if (!dateStr) return null;
    const d = new Date(`${dateStr}T00:00:00`);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split("T")[0];
  };

  const period1Label = firstDate ? formatMonthYear(firstDate) : "";
  const period2Label = firstDate
    ? formatMonthYear(addMonths(firstDate, 1))
    : "";
  const period3Label = firstDate
    ? formatMonthYear(addMonths(firstDate, 7))
    : "";

  const part1 = `A 1ª parcela${period1Label ? ` em ${period1Label}` : ""} será de ${formatCurrencyBRL(v1)}`;

  if (v2 > 0 && v3 > 0 && Math.abs(v2 - v3) < 0.01) {
    const fromLabel = period2Label ? ` a partir de ${period2Label}` : "";
    const remainingCount = totalParcelas > 1 ? totalParcelas - 1 : 23;
    return `${part1} + ${remainingCount} parcelas${fromLabel} no valor de ${formatCurrencyBRL(v2)}`;
  }

  const period3Count = totalParcelas > 7 ? totalParcelas - 7 : 12;

  let result = part1;
  if (v2 > 0)
    result += ` + 6 parcelas${period2Label ? ` a partir de ${period2Label}` : ""} no valor de ${formatCurrencyBRL(v2)}`;
  if (v3 > 0)
    result += ` + ${period3Count} parcelas${period3Label ? ` a partir de ${period3Label}` : ""} no valor de ${formatCurrencyBRL(v3)}`;
  return result;
};

export const onlyDigits = (value) => (value || "").replace(/\D/g, "");

/** "1990-05-20" → "20/05/1990" */
export const isoToBR = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

/** "20/05/1990" → "1990-05-20"; incomplete input passes through unchanged */
export const brToISO = (br) => {
  const digits = br.replace(/\D/g, "");
  if (digits.length < 8) return br;
  return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
};

/** Live mask: auto-inserts '/' at positions 2 and 5 while user types */
export const maskDateBRLive = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const TIPO_ACEITE_LABELS = {
  acordo: "Aceitou o Acordo",
  cessao: "Aceitou a Cessão",
};

export const getTipoAceiteLabel = (tipoAceite) =>
  TIPO_ACEITE_LABELS[tipoAceite] ?? "Pendente";

export const getTipoAceiteClass = (tipoAceite) => {
  if (tipoAceite === "cessao") return "bg-green-100 text-green-700";
  if (tipoAceite === "acordo") return "bg-blue-100 text-blue-700";
  return "bg-yellow-100 text-yellow-800";
};
